import React, { useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { Download, Check, X, Loader2, Lightbulb, Edit, ThumbsUp, ThumbsDown, Image as ImageIcon } from "lucide-react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { Link, useNavigate } from "react-router-dom";
import { MyClipQuery, MyClipQueryVariables } from "@/generated/graphql";
import ShareClipButton from "@/components/ShareClipButton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useClipPolling } from "@/hooks/use-polling";
import { useToast } from "@/hooks/use-toast";
import { getClipStatusText } from "@/lib/get-clip-status-text";
import { formatDateTime, formatDuration } from "@/lib/format";
import { downloadClipVideo } from "@/lib/downloads/clip-download";
import { GET_MY_CLIP, RATE_CLIP } from "@/queries/queries";
import ClipTitlesDialog from "@/components/ClipTitlesDialog";
import ClipVersionBadge, { VersionBadgeClip } from "@/components/ClipVersionBadge";
import type { VersionGroup } from "@/lib/clips/groupClipVersions";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ClipPublishingControls } from "@/components/publishing/ClipPublishingControls";
import { EditWithAiButton } from "@/components/assistant/EditWithAiButton";
import { ClipCoverDialog } from "@/components/clips/cover/ClipCoverDialog";
import {
  canSetClipCover,
  resolveClipCardCoverUrl,
  type ClipCoverOverride,
} from "@/lib/cover/clipCardCover";
import { aiStudioPathFor } from "@/lib/ai-assistant/aiStudioPaths";
import { canMakeQuoteCarousel } from "@/lib/quoteCarousel/canMakeQuoteCarousel";
import { PublishStatusOverlay } from "@/components/publishing/PublishStatusOverlay";
import type { PublishMediaKinds, PublishUiStatus } from "@/lib/publishing/types";
import { useDownloadUpsell } from "@/contexts/downloadUpsellContext";

// Utility function to format ETA seconds
const formatEta = (etaSeconds: number | null | undefined, t: (key: string, options?: any) => string): string => {
  if (!etaSeconds || etaSeconds <= 0) return "";

  const minutes = Math.floor(etaSeconds / 60);
  const seconds = Math.round(etaSeconds % 60);

  if (minutes > 0 && seconds > 0) {
    return `${t('clipCard.estimate')} ${minutes}m ${seconds}s`;
  } else if (minutes > 0) {
    return `${t('clipCard.estimate')} ${minutes}m`;
  } else {
    return `${t('clipCard.estimate')} ${seconds}s`;
  }
};

export interface ClipCardProps {
  clip: MyClipQuery['myClip'];
  versionGroup?: VersionGroup<VersionBadgeClip>;
  onSendToQueue?: (clipId: string) => void;
  onSendToTikTok?: (clipId: string) => void;
  onPublishToYouTube?: (clipId: string) => void;
  onPublishToInstagram?: (clipId: string) => void;
  onPublishToFacebook?: (clipId: string) => void;
  tiktokPublishStatus?: PublishUiStatus;
  tiktokPublishFailReason?: string | null;
  youtubePublishStatus?: PublishUiStatus;
  youtubePublishFailReason?: string | null;
  instagramPublishStatus?: PublishUiStatus;
  instagramPublishFailReason?: string | null;
  facebookPublishStatus?: PublishUiStatus;
  facebookPublishFailReason?: string | null;
  /** Latest publish media_kind per platform ('carousel' adds an icon). */
  publishMediaKinds?: PublishMediaKinds;
  /** Offer "Editar com IA" (the page knows whether the viewer has it). */
  showEditWithAi?: boolean;
  /** Whether the viewer has PRO features (gates the AI cover). */
  hasProFeatures?: boolean;
}

const PENDING_STATUSES = ['processing', 'pre-queued', 'queued'];

export const ClipCard: React.FC<ClipCardProps> = ({
  clip,
  versionGroup,
  onSendToQueue,
  onSendToTikTok,
  onPublishToYouTube,
  onPublishToInstagram,
  onPublishToFacebook,
  tiktokPublishStatus,
  tiktokPublishFailReason,
  youtubePublishStatus,
  youtubePublishFailReason,
  instagramPublishStatus,
  instagramPublishFailReason,
  facebookPublishStatus,
  facebookPublishFailReason,
  publishMediaKinds,
  showEditWithAi = false,
  hasProFeatures = false,
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { notifyClipDownloaded } = useDownloadUpsell();
  const [rateClipMutation] = useMutation(RATE_CLIP);
  const navigate = useNavigate()
  // Download progress state
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);

  // Titles dialog state
  const [titlesDialogOpen, setTitlesDialogOpen] = useState(false);

  // Video preview modal state
  const [videoPreviewOpen, setVideoPreviewOpen] = useState(false);

  // Warning modal state
  const [warningModalOpen, setWarningModalOpen] = useState(false);

  // Download error modal state
  const [downloadErrorOpen, setDownloadErrorOpen] = useState(false);

  // Rating state
  const [clipRating, setClipRating] = useState<string>(clip.curation_rating || 'neutral');

  // Cover: this session's saved cover wins over the fetched one
  const [coverOverride, setCoverOverride] = useState<ClipCoverOverride>(null);
  const [coverDialogOpen, setCoverDialogOpen] = useState(false);

  // Thumbnail error state for expired clips
  const [thumbnailError, setThumbnailError] = useState(false);
  // The saved cover image failed to load (separate from the video/thumbnail ladder)
  const [failedCoverUrl, setFailedCoverUrl] = useState<string | null>(null);

  const handleThumbnailError = useCallback(() => {
    setThumbnailError(true);
  }, []);

    // Lazy query to get fresh clip data only when user clicks the button
  const [fetchFreshClip, { data: freshClipData }] = useLazyQuery<MyClipQuery, MyClipQueryVariables>(
    GET_MY_CLIP,
    {
      fetchPolicy: 'network-only', // Always get fresh data from server when triggered
      notifyOnNetworkStatusChange: true,
    }
  );

  // Use polling hook to get real-time updates when clip is processing
  const { data: polledClip } = useClipPolling(
    clip.id,
    {
      enabled: (
        PENDING_STATUSES.includes(clip.clipping_status) ||
        PENDING_STATUSES.includes(clip.preparing_status) ||
        PENDING_STATUSES.includes(clip.rendering_status)
      ),
      interval: 5000,
    }
  );

  // Use polled data if available, otherwise fall back to props
  const currentClip = polledClip || clip;

  // Determine current status based on polled data or props
  const currentStatusState = useMemo(() => {
    if (currentClip.rendering_status !== 'skipped') {
      return currentClip.rendering_status
    }
    if (currentClip.preparing_status !== 'skipped') {
      return currentClip.preparing_status
    }
    return currentClip.clipping_status
  }, [currentClip.clipping_status, currentClip.preparing_status, currentClip.rendering_status]);

  const effectiveStatusLabel = getClipStatusText(currentClip as any);

  const effectiveDownloadUrl = currentClip.finished_video_url
    || currentClip.compressed_video_url
    || currentClip.clipped_video_url;
  const displayName = (currentClip as any)?.name || clip.name;

  // Get ETA from polled data or original clip data
  const etaSeconds = polledClip?.eta_seconds || clip.eta_seconds;
  const etaText = formatEta(etaSeconds, t);

  // Check if clip is expired
  const isExpired = useMemo(() => {
    return (
      currentClip.clipping_status === 'expired' ||
      currentClip.preparing_status === 'expired' ||
      currentClip.rendering_status === 'expired'
    );
  }, [currentClip.clipping_status, currentClip.preparing_status, currentClip.rendering_status]);

  const aiStudioUrl = useMemo(() => {
    if (clip.is_library_clip && clip.library_id) {
      return aiStudioPathFor({ kind: 'library', id: clip.library_id, clipId: clip.id });
    }
    if (!clip.is_library_clip && clip.project_id) {
      return aiStudioPathFor({ kind: 'project', id: clip.project_id, clipId: clip.id });
    }
    return null;
  }, [clip.id, clip.is_library_clip, clip.project_id, clip.library_id]);

  // Compute edit URL: project clips go to project editor, library clips go to library editor
  const editUrl = useMemo(() => {
    if (clip.is_library_clip && clip.library_id) {
      return `/library/${clip.library_id}/edit?clip=${clip.id}`;
    }
    if (!clip.is_library_clip && clip.project_id) {
      return `/project/${clip.project_id}/edit?clip=${clip.id}`;
    }
    return null;
  }, [clip.id, clip.is_library_clip, clip.project_id, clip.library_id]);

  const getStatusClasses = () => {
    if (currentStatusState === 'success') {
      return 'bg-gradient-primary bg-clip-text text-transparent font-medium';
    } else if (currentStatusState === 'failure') {
      return 'text-red-500';
    } else if (currentStatusState === 'expired') {
      return 'text-amber-500';
    }
    return 'text-muted-foreground';
  };

  const handleDownload = async () => {
    if (!effectiveDownloadUrl || isDownloading) return;

    // Check if we need to show warning modal
    if (currentClip.preparing_status === 'success' && currentClip.rendering_status === 'skipped') {
      setWarningModalOpen(true);
      return;
    }

    performDownload();
  };

  const performDownload = async () => {
    if (!effectiveDownloadUrl || isDownloading) return;

    setIsDownloading(true);
    setDownloadProgress(0);

    try {
      await downloadClipVideo({
        clipId: currentClip.id,
        fallbackName: displayName || "clip",
        downloadUrl: effectiveDownloadUrl,
        onDownloadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentage = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setDownloadProgress(percentage);
          }
        },
      });
      notifyClipDownloaded();
    } catch (error) {
      console.error('Download failed:', error);
      setDownloadErrorOpen(true);
    } finally {
      setIsDownloading(false);
      setDownloadProgress(0);
    }
  };

  const isCurrentStateSuccess = currentStatusState === 'success'
  const isCurrentStateFailure = currentStatusState === 'failure'
  const isCurrentStateExpired = currentStatusState === 'expired'
  const isCurrentStateProcessing = !isCurrentStateSuccess && !isCurrentStateFailure && !isCurrentStateExpired
  const coverUrl = resolveClipCardCoverUrl({
    fetchedUrl: currentClip.cover_image_url,
    override: coverOverride,
  });
  const showCoverAction = canSetClipCover({
    canSetCover: currentClip.can_set_cover,
    finishedVideoUrl: currentClip.finished_video_url,
    isRendered: isCurrentStateSuccess,
    isExpired,
  });
  const canShowPublishActions = isCurrentStateSuccess && !!effectiveDownloadUrl && !isExpired;

  const handleRating = async (rating: 'like' | 'dislike') => {
    try {
      const newRating = clipRating === rating ? 'neutral' : rating;
      await rateClipMutation({ variables: { id: clip.id, rating: newRating } });
      setClipRating(newRating);
      toast({
        title: newRating === 'neutral' ? t('projectEditor.clipRatingRemoved') : t('projectEditor.clipRated'),
      });
    } catch (error) {
      console.error('Error rating clip:', error);
      toast({ title: t('projectEditor.clipRatingError'), variant: "destructive" });
    }
  };

  return (
    <Card key={clip.id} className={`overflow-hidden ${isExpired ? 'cursor-not-allowed' : ''}`}>
      <CardHeader className={`pb-3 ${isExpired ? 'opacity-20' : ''}`}>
        <div className="flex justify-between items-start gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 min-w-0">
              <CardTitle className="text-base truncate">{currentClip.name || t('clipCard.noName')}</CardTitle>
              <ClipVersionBadge
                versionGroup={versionGroup}
                currentClipId={currentClip.id}
                currentVersionNumber={(currentClip as VersionBadgeClip).version_number}
              />
            </div>
            <CardDescription className="text-xs flex gap-2 items-center">
              <span>{formatDateTime(clip.createdAt)}</span>
              <span>•</span>
              <span>{formatDuration(clip.duration_seconds)}</span>
            </CardDescription>
          </div>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  if (isExpired) return;
                  // Fetch fresh clip data to ensure we have latest copy information
                  fetchFreshClip({ variables: { id: clip.id } });
                  setTitlesDialogOpen(true);
                }}
                disabled={isExpired}
                className={`h-8 w-8 p-0 flex-shrink-0 border-gradient-primary/20 ${!isExpired ? 'hover:bg-gradient-hero' : ''}`}
              >
                <Lightbulb className="w-4 h-4 text-green-500" />
              </Button>
            </TooltipTrigger>
            {!isExpired && (
              <TooltipContent side="top" sideOffset={2}>
                {t('clipCard.titleGenerator')}
              </TooltipContent>
            )}
          </Tooltip>
        </div>
      </CardHeader>
      <CardContent className={`pt-0 ${isExpired ? 'opacity-20' : ''}`}>
        <div className="relative">
        {currentClip.finished_video_url && !thumbnailError ? (
          <div
            className={`relative aspect-video w-full rounded-md bg-black overflow-hidden ${!isExpired ? 'cursor-pointer hover:opacity-90' : ''} transition-opacity`}
            onClick={() => !isExpired && effectiveDownloadUrl && setVideoPreviewOpen(true)}
          >
            <video
              src={currentClip.finished_video_url}
              poster={coverUrl ?? undefined}
              preload="metadata"
              playsInline
              muted
              className="w-full h-full object-contain"
              onError={handleThumbnailError}
            />
            {effectiveDownloadUrl && !isExpired && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 hover:opacity-100 transition-opacity">
                <div className="bg-white/90 rounded-full p-3">
                  <svg className="w-6 h-6 text-black" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                </div>
              </div>
            )}
          </div>
        ) : currentClip.finished_video_thumbnail_url && !thumbnailError ? (
          <div
            className={`relative aspect-video w-full rounded-md overflow-hidden ${!isExpired ? 'cursor-pointer hover:opacity-90' : ''} transition-opacity`}
            onClick={() => !isExpired && effectiveDownloadUrl && setVideoPreviewOpen(true)}
          >
            <img
              src={coverUrl ?? currentClip.finished_video_thumbnail_url}
              alt="Thumbnail do clipe"
              className="w-full h-full object-cover"
              onError={handleThumbnailError}
            />
            {effectiveDownloadUrl && !isExpired && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 hover:opacity-100 transition-opacity">
                <div className="bg-white/90 rounded-full p-3">
                  <svg className="w-6 h-6 text-black" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                </div>
              </div>
            )}
          </div>
        ) : currentClip.thumbnail_url && !thumbnailError ? (
          <div
            className={`relative aspect-video w-full rounded-md overflow-hidden ${!isExpired ? 'cursor-pointer hover:opacity-90' : ''} transition-opacity`}
            onClick={() => !isExpired && effectiveDownloadUrl && setVideoPreviewOpen(true)}
          >
            <img
              src={currentClip.thumbnail_url}
              alt="Thumbnail do clipe"
              className="w-full h-full object-cover"
              onError={handleThumbnailError}
            />
            {effectiveDownloadUrl && !isExpired && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 hover:opacity-100 transition-opacity">
                <div className="bg-white/90 rounded-full p-3">
                  <svg className="w-6 h-6 text-black" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                </div>
              </div>
            )}
          </div>
        ) : coverUrl && failedCoverUrl !== coverUrl ? (
          // A video error must not hide the cover the user saved.
          <div
            className={`relative aspect-video w-full rounded-md bg-black overflow-hidden ${!isExpired ? 'cursor-pointer hover:opacity-90' : ''} transition-opacity`}
            onClick={() => !isExpired && effectiveDownloadUrl && setVideoPreviewOpen(true)}
          >
            <img
              src={coverUrl}
              alt="Thumbnail do clipe"
              className="w-full h-full object-contain"
              onError={() => setFailedCoverUrl(coverUrl)}
            />
            {effectiveDownloadUrl && !isExpired && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 hover:opacity-100 transition-opacity">
                <div className="bg-white/90 rounded-full p-3">
                  <svg className="w-6 h-6 text-black" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                </div>
              </div>
            )}
          </div>
        ) : (
          <Skeleton className="aspect-video w-full rounded-md" />
        )}
        {!isExpired && (
          <PublishStatusOverlay
            className="absolute top-2 left-2 z-20"
            statuses={[
              {
                platform: 'tiktok',
                status: tiktokPublishStatus,
                failReason: tiktokPublishFailReason,
                mediaKind: publishMediaKinds?.tiktok,
              },
              {
                platform: 'instagram',
                status: instagramPublishStatus,
                failReason: instagramPublishFailReason,
                mediaKind: publishMediaKinds?.instagram,
              },
              {
                platform: 'facebook',
                status: facebookPublishStatus,
                failReason: facebookPublishFailReason,
                mediaKind: publishMediaKinds?.facebook,
              },
              {
                platform: 'youtube',
                status: youtubePublishStatus,
                failReason: youtubePublishFailReason,
                mediaKind: publishMediaKinds?.youtube,
              },
            ]}
          />
        )}
        </div>
      </CardContent>
      <CardFooter className="flex-col gap-3">
        {isDownloading && (
          <div className="w-full space-y-1">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{t('clipCard.downloading')}</span>
              <span>{downloadProgress}%</span>
            </div>
            <Progress value={downloadProgress} className="h-2 [&>div]:bg-gradient-primary" />
          </div>
        )}
        <div className="flex justify-between w-full">
          <div className={`flex flex-col justify-center gap-0.5 text-xs ${isExpired ? 'opacity-20' : ''}`}>
            <span className={`flex items-center gap-1 ${getStatusClasses()}`}>
              {isCurrentStateSuccess && <Check className="w-3.5 h-3.5 text-green-500" />}
              {isCurrentStateFailure && <X className="w-3.5 h-3.5 text-red-500" />}
              {isExpired && <X className="w-3.5 h-3.5 text-amber-500" />}
              {isCurrentStateProcessing && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-500" />}
              {effectiveStatusLabel}
            </span>
            {etaText && !isCurrentStateSuccess && !isCurrentStateFailure && !isExpired && (
              <span className="text-muted-foreground text-xs">{etaText}</span>
            )}
          </div>
          <div className="flex gap-2 items-center">
            {isCurrentStateSuccess && (
              <>
                <button
                  className={`w-7 h-7 rounded flex items-center justify-center transition-colors ${clipRating === 'like' ? 'bg-green-500 text-white' : 'bg-muted text-muted-foreground hover:text-green-500'}`}
                  onClick={() => handleRating('like')}
                >
                  <ThumbsUp className="w-4 h-4" />
                </button>
                <button
                  className={`w-7 h-7 rounded flex items-center justify-center transition-colors ${clipRating === 'dislike' ? 'bg-red-500 text-white' : 'bg-muted text-muted-foreground hover:text-red-500'}`}
                  onClick={() => handleRating('dislike')}
                >
                  <ThumbsDown className="w-4 h-4" />
                </button>
              </>
            )}
            {showCoverAction && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid="clip-cover-action"
                    aria-label={t('clipCover.action')}
                    onClick={() => setCoverDialogOpen(true)}
                  >
                    <ImageIcon className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" sideOffset={2}>
                  {t('clipCover.action')}
                </TooltipContent>
              </Tooltip>
            )}
            {editUrl && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link
                    to={editUrl}
                    className={isExpired ? 'opacity-50 pointer-events-none' : ''}
                    onClick={(event) => {
                      if (isExpired) {
                        event.preventDefault();
                        return;
                      }
                    }}
                  >
                    <Button
                      variant="outline-gradient"
                      size="sm"
                      disabled={isExpired}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                  </Link>
                </TooltipTrigger>
                {!isExpired && (
                  <TooltipContent side="bottom" sideOffset={2}>
                    {t('clipCard.editVideo')}
                  </TooltipContent>
                )}
              </Tooltip>
            )}
            {showEditWithAi && aiStudioUrl && (
              <EditWithAiButton
                className="h-9 w-9"
                disabled={isExpired}
                onClick={() => navigate(aiStudioUrl)}
              />
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  onClick={handleDownload}
                  disabled={!effectiveDownloadUrl || isDownloading || isExpired}
                  variant="gradient"
                >
                  {isDownloading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                </Button>
              </TooltipTrigger>
              {!isExpired && (
                <TooltipContent
                  side="bottom"
                  sideOffset={2}
                >
                  {t('clipCard.downloadVideo')}
                </TooltipContent>
              )}
            </Tooltip>
            {effectiveDownloadUrl && !isExpired && (
              <ShareClipButton
                downloadUrl={effectiveDownloadUrl}
                clipName={displayName || t('clipCard.noName')}
                className="h-9"
              />
            )}
          </div>
        </div>
        <ClipPublishingControls
          clipId={clip.id}
          canPublish={canShowPublishActions}
          canMakeCarousel={canMakeQuoteCarousel(currentClip)}
          onSendToQueue={onSendToQueue}
          onSendToTikTok={onSendToTikTok}
          onPublishToYouTube={onPublishToYouTube}
          onPublishToInstagram={onPublishToInstagram}
          onPublishToFacebook={onPublishToFacebook}
          tiktokPublishStatus={tiktokPublishStatus}
          tiktokPublishFailReason={tiktokPublishFailReason}
          youtubePublishStatus={youtubePublishStatus}
          youtubePublishFailReason={youtubePublishFailReason}
          instagramPublishStatus={instagramPublishStatus}
          instagramPublishFailReason={instagramPublishFailReason}
          facebookPublishStatus={facebookPublishStatus}
          facebookPublishFailReason={facebookPublishFailReason}
        />
      </CardFooter>

      {/* Video Preview Modal */}
      <Dialog open={videoPreviewOpen} onOpenChange={setVideoPreviewOpen}>
        <DialogContent className="max-w-4xl w-full">
          <DialogHeader>
            <DialogTitle>{currentClip.name || t('clipCard.videoPreview')}</DialogTitle>
          </DialogHeader>
          {effectiveDownloadUrl && (
            <div className="relative aspect-video w-full rounded-md bg-black overflow-hidden">
              <video
                src={effectiveDownloadUrl}
                controls
                autoPlay
                playsInline
                className="w-full h-full object-contain"
              >
                {t('clipCard.browserNotSupportVideo')}
              </video>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Warning Modal */}
      <Dialog open={warningModalOpen} onOpenChange={setWarningModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('clipCard.videoNotEdited')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {t('clipCard.videoNotEditedDesc')}
            </p>
            <p className="text-sm text-muted-foreground">
              {t('clipCard.addSubtitlesDesc')}
            </p>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setWarningModalOpen(false)}
              >
                {t('common.cancel')}
              </Button>
              {editUrl && (
                <Button
                  variant="secondary"
                  onClick={() => {
                    setWarningModalOpen(false);
                    navigate(editUrl);
                  }}
                >
                  <Edit className="w-4 h-4 mr-2" />
                  {t('clipCard.editVideoButton')}
                </Button>
              )}
              <Button
                onClick={() => {
                  setWarningModalOpen(false);
                  performDownload();
                }}
              >
                {t('clipCard.downloadAnyway')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Download Error Modal (popup blocked fallback) */}
      <Dialog open={downloadErrorOpen} onOpenChange={setDownloadErrorOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('clipCard.downloadFailedTitle')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {t('clipCard.downloadFailedDesc')}
            </p>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setDownloadErrorOpen(false)}
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={() => {
                  setDownloadErrorOpen(false);
                  performDownload();
                }}
              >
                {t('common.retry')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ClipTitlesDialog
        open={titlesDialogOpen}
        onOpenChange={setTitlesDialogOpen}
        clipId={clip.id}
        clipName={currentClip.name || t('clipCard.noName')}
        existingCopy={freshClipData?.myClip?.copy || currentClip.copy}
      />

      {coverDialogOpen && currentClip.finished_video_url && (
        <ClipCoverDialog
          key={currentClip.id}
          open={coverDialogOpen}
          onOpenChange={setCoverDialogOpen}
          clipId={currentClip.id}
          clipName={displayName ?? null}
          videoUrl={currentClip.finished_video_url}
          coverUrl={coverUrl}
          hasProAccess={hasProFeatures}
          onSaved={(url) => setCoverOverride({ url })}
        />
      )}
    </Card>
  );
};

export default ClipCard;
